import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft } from 'lucide-react'
import MarketingNav from '../components/MarketingNav'
import SiteFooter from '../components/SiteFooter'
import '../components/marketing-dark.css'
import { CATALOG, byKey, unitOf, isSeated } from '../lib/serviceCatalog'
import { slugFor, clusterFor } from '../components/marketingMeta'
import { getCart, setQty, removeFromCart, cartCount } from '../lib/cart'

// Public, editable cart — review services, change seats, remove, then checkout.
// The cart lives in localStorage (../lib/cart); no login needed to review it.
export default function Cart() {
  const navigate = useNavigate()
  const [cart, setCart] = useState(getCart())
  const [seats, setSeats] = useState(cartCount())
  useEffect(() => {
    const sync = () => { setCart(getCart()); setSeats(cartCount()) }
    window.addEventListener('reright-cart', sync)
    window.addEventListener('storage', sync)
    return () => { window.removeEventListener('reright-cart', sync); window.removeEventListener('storage', sync) }
  }, [])

  // Services for the mega-nav (from the local catalog + slug/cluster map — no fetch).
  const navServices = CATALOG.map((s) => ({
    key: s.key, name: s.name, short: s.short, price: s.price, icon: s.icon,
    slug: slugFor(s.key), cluster: clusterFor(s.key),
  }))

  const items = cart.map((c) => { const s = byKey(c.key); return s ? { ...s, qty: c.qty } : null }).filter(Boolean)
  const total = items.reduce((s, x) => s + x.price * x.qty, 0)
  const goProducts = useCallback(() => navigate('/products'), [navigate])

  return (
    <div className="mkt min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      <MarketingNav services={navServices} cartCount={seats} onOpenCart={() => {}}
        signIn={{ router: false, to: 'https://ai.reright.io/login', label: 'Sign in' }} />

      <main className="flex-1 max-w-3xl w-full mx-auto px-5 py-12 sm:py-16">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-primary-500/10 text-primary-600 flex items-center justify-center"><ShoppingCart size={18} /></span>
          <h1 className="text-2xl font-display font-black tracking-tight">Your cart</h1>
        </div>
        <p className="text-gray-500 mt-1.5">Review your services, adjust seats, then check out. Cancel any service anytime.</p>

        {items.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-gray-200 bg-white shadow-sm text-center py-16 px-6">
            <ShoppingCart size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-600 font-medium">Your cart is empty.</p>
            <p className="text-gray-400 text-sm mt-1">Browse the 15 services and add what you need.</p>
            <button onClick={goProducts} className="btn-primary mt-5 inline-flex items-center gap-1.5">Browse services <ArrowRight size={16} /></button>
          </div>
        ) : (
          <>
            <div className="mt-8 rounded-2xl border border-gray-200 bg-white shadow-sm divide-y divide-gray-100">
              {items.map((s) => {
                const Icon = s.icon
                const seated = isSeated(s.key)
                return (
                  <div key={s.key} className="flex items-center gap-4 p-4 sm:p-5">
                    <span className="w-11 h-11 shrink-0 rounded-xl bg-primary-500/10 flex items-center justify-center"><Icon className="text-primary-600" size={20} /></span>
                    <div className="flex-1 min-w-0">
                      <Link to={`/products/${slugFor(s.key)}`} className="text-[15px] font-semibold text-gray-900 hover:text-primary-600">{s.name}</Link>
                      <p className="text-xs text-gray-500 truncate mt-0.5">${s.price}/mo{seated ? ` · per ${unitOf(s.key)}` : ''}{s.short ? ` · ${s.short}` : ''}</p>
                    </div>
                    {seated ? (
                      <div className="flex items-center rounded-full border border-gray-200 bg-gray-50">
                        <button aria-label="Fewer" onClick={() => setQty(s.key, Math.max(1, s.qty - 1))} className="w-8 h-8 grid place-items-center text-gray-500 hover:text-gray-900 disabled:opacity-40" disabled={s.qty <= 1}><Minus size={14} /></button>
                        <span className="w-8 text-center text-sm font-semibold tabular-nums">{s.qty}</span>
                        <button aria-label="More" onClick={() => setQty(s.key, s.qty + 1)} className="w-8 h-8 grid place-items-center text-gray-500 hover:text-gray-900"><Plus size={14} /></button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 hidden sm:inline">1 workspace</span>
                    )}
                    <span className="w-20 text-right text-sm font-semibold text-gray-900 tabular-nums">${s.price * s.qty}<span className="text-gray-400 font-normal">/mo</span></span>
                    <button aria-label={`Remove ${s.name}`} onClick={() => removeFromCart(s.key)} className="text-gray-300 hover:text-red-500 p-1.5"><Trash2 size={16} /></button>
                  </div>
                )
              })}
            </div>

            <div className="mt-5 rounded-2xl border border-gray-200 bg-white shadow-sm p-5 sm:p-6">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">{items.length} service{items.length > 1 ? 's' : ''}{seats > items.length ? ` · ${seats} seats` : ''}</span>
                <span className="text-2xl font-display font-black">${total}<span className="text-gray-400 text-base font-normal">/mo</span></span>
              </div>
              <button onClick={() => navigate('/checkout')} className="btn-primary w-full mt-4 flex items-center justify-center gap-2 py-3 text-base">Proceed to checkout <ArrowRight size={18} /></button>
              <p className="text-xs text-center text-gray-400 mt-2.5">Sign in at checkout to activate — it’s free to create your account. Secure payment via Stripe.</p>
            </div>

            <button onClick={goProducts} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mt-5"><ArrowLeft size={15} /> Add more services</button>
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  )
}
